import { FunctionComponent } from "react";
import { Box, Circle, Flex, HStack, Icon, Text, VStack } from "@chakra-ui/react";
import { formatDateStr, formatTimestampStr } from "@am-tax/fe-core";
import { CandidateStatusDetails } from "../../components/CandidateStatus";
import { CandidateActivity, EntityCandidate } from "../../api/models/EntityCandidate";
import { IconBriefcase, IconCalendarOff, IconPlayerPlay, IconUserCheck } from "@tabler/icons-react";
import { FC } from "react";
import { useUserContext } from "../../context";

type EmploymentEvent = {
    kind: "employment";
    date: string;
    label: string;
    icon: FC;
    color: string;
};

type StatusEvent = {
    kind: "status";
    activity: CandidateActivity;
};

type TimelineEvent = (EmploymentEvent | StatusEvent) & { sortKey: string };

const ZERO_DATE_PREFIX = "0001-01-01";

function buildEmploymentEvents(candidate: EntityCandidate | undefined): EmploymentEvent[] {
    if (!candidate) return [];
    const events: EmploymentEvent[] = [];

    const add = (date: string | undefined, label: string, icon: FC, color: string) => {
        if (date && !date.startsWith(ZERO_DATE_PREFIX)) {
            events.push({ kind: "employment", date, label, icon, color });
        }
    };

    add(candidate.employmentOfferDate, "Offer Extended", IconBriefcase, "blue.500");
    add(candidate.employmentHireDate, "Hired", IconUserCheck, "green.500");
    add(candidate.employmentStartDate, "Employment Started", IconPlayerPlay, "teal.500");
    add(candidate.employmentTerminationDate, "Employment Ended", IconCalendarOff, "orange.600");

    return events;
}

export const CandidateTimeline: FunctionComponent<{
    candidateActivity: CandidateActivity[] | null | undefined;
    candidate?: EntityCandidate;
}> = ({ candidateActivity, candidate }) => {
    const { dateFormat } = useUserContext();

    const statusEvents: TimelineEvent[] = (candidateActivity ?? []).map(activity => ({
        kind: "status",
        activity,
        sortKey: activity.createdOn ?? "",
    }));

    const employmentEvents: TimelineEvent[] = buildEmploymentEvents(candidate).map(e => ({
        ...e,
        sortKey: e.date,
    }));

    const events = [...statusEvents, ...employmentEvents].sort((a, b) => a.sortKey.localeCompare(b.sortKey));

    if (events.length === 0) {
        return (
            <Flex justify="center" align="center" py={8}>
                <Text color="gray.500">No timeline events found.</Text>
            </Flex>
        );
    }

    return (
        <VStack align="stretch" spacing={0}>
            {events.map((event, index) => (
                <TimelineRow key={index} event={event} isLast={index === events.length - 1} dateFormat={dateFormat} />
            ))}
        </VStack>
    );
};

const TimelineRow: FunctionComponent<{ event: TimelineEvent; isLast: boolean; dateFormat: string }> = ({ event, isLast, dateFormat }) => {
    if (event.kind === "status") {
        const { activity } = event;
        const details = activity.newStatus ? CandidateStatusDetails[activity.newStatus] : undefined;
        const color = details?.color ?? "gray.400";
        const iconAs = details?.as;
        const title = details?.title ?? activity.newStatus;
        const dateStr = formatTimestampStr(activity.createdOn);
        const user = activity.createdBy?.displayName;

        return <TimelineItem color={color} iconAs={iconAs} title={`Status: ${title}`} date={dateStr} user={user} isLast={isLast} />;
    }

    const { label, icon, color, date } = event as EmploymentEvent & { sortKey: string };
    return <TimelineItem color={color} iconAs={icon} title={label} date={formatDateStr(date, dateFormat)} isLast={isLast} />;
};

const TimelineItem: FunctionComponent<{
    color: string;
    iconAs: FC | undefined;
    title: string;
    date: string | null | undefined;
    user?: string | null;
    isLast: boolean;
}> = ({ color, iconAs, title, date, user, isLast }) => {
    const colorBase = color.split(".")[0];
    return (
        <HStack align="stretch" spacing={0}>
            {/* Connector column */}
            <VStack spacing={0} align="center" width="2.5rem" flexShrink={0}>
                <Circle size="2rem" bg={`${colorBase}.50`} border="2px solid" borderColor={color}>
                    {iconAs && <Icon as={iconAs} color={color} boxSize="1rem" />}
                </Circle>
                {!isLast && <Box flex={1} width="2px" bg="gray.200" minH="1.5rem" my="0.25rem" />}
            </VStack>

            {/* Content column */}
            <Box pb={isLast ? 0 : 6} pl={4} pt="0.25rem">
                <Text fontWeight="semibold" fontSize="sm" lineHeight="short">
                    {title}
                </Text>
                <Text fontSize="xs" color="gray.500" mt={0.5}>
                    {[date, user].filter(Boolean).join(" · ")}
                </Text>
            </Box>
        </HStack>
    );
};
